import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { useDataService } from '../context/DataContext';
import useResponsive from '../hooks/useResponsive';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const { isMobile } = useResponsive();
  const dataService = useDataService();

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const postsData = await dataService.getPosts({ limit: 5, page: currentPage });
        // Handle both array and object with posts property
        const postsList = Array.isArray(postsData) 
          ? postsData 
          : (postsData.posts || []);
        // Sort by creation date (latest first) to ensure proper ordering
        const sortedPosts = [...postsList].sort((a, b) => {
          const dateA = new Date(a.created_at || a.published_at || 0);
          const dateB = new Date(b.created_at || b.published_at || 0);
          return dateB - dateA; // Latest first
        });
        setPosts(sortedPosts);
        
        // Calculate total pages and store total count
        if (postsData.total !== undefined) {
          setTotalPosts(postsData.total);
          setTotalPages(Math.ceil(postsData.total / 5));
        } else {
          // If no total, use the length of posts as fallback
          setTotalPosts(postsList.length);
          setTotalPages(1);
        }
      } catch (err) {
        console.error('Error loading posts:', err);
        setError(err.message);
        // Fallback to mock data
        try {
          const { blogPosts: mockPosts } = await import('../data/mock');
          const startIndex = (currentPage - 1) * 5;
          const endIndex = startIndex + 5;
          setPosts(mockPosts.slice(startIndex, endIndex));
          setTotalPosts(mockPosts.length);
          setTotalPages(Math.ceil(mockPosts.length / 5));
        } catch (mockErr) {
          console.error('Error loading mock posts:', mockErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [dataService, currentPage]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load posts</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <Link 
          to="/"
          className="text-gray-900 hover:text-gray-700 font-medium transition-colors"
        >
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation */}
      <nav className="mb-8">
        <Link 
          to="/" 
          className="text-vintage-faded hover:text-vintage-accent flex items-center space-x-2 transition-colors"
        >
          <ArrowLeft size={16} />
            <span>Back</span>
        </Link>
      </nav>

      {/* Header */}
      <header className="mb-12 text-center">
        <h1 className="vintage-heading-1 text-4xl md:text-5xl text-vintage-ink mb-4">
          Blogs & Essays
        </h1>
        <p className="vintage-body text-vintage-faded max-w-2xl mx-auto">
          Insights on venture capital, technology, and strategic leadership
        </p>
      </header>

      {/* Posts List */}
      {posts.length > 0 ? (
        <>
          <div className="space-y-8">
            {posts.map((post) => (
              <article key={post.id} className="vintage-card p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4 text-sm text-vintage-faded mb-4">
                  <div className="flex items-center space-x-1">
                    <Calendar size={14} />
                    <span>{formatDate(post.published_at || post.publishedAt)}</span>
                  </div>
                  {post.read_time && (
                    <div className="flex items-center space-x-1">
                      <Clock size={14} />
                      <span>{post.read_time}</span>
                    </div>
                  )}
                  {post.featured && (
                    <span className="vintage-badge bg-vintage-accent text-vintage-cream">
                      Featured
                    </span>
                  )}
                  {post.category && (
                    <span className="vintage-badge">
                      {post.category}
                    </span>
                  )}
                </div>
                
                <Link to={`/post/${post.slug}`} className="group">
                  <h3 className="vintage-heading-2 text-2xl text-vintage-ink group-hover:text-vintage-accent transition-colors mb-3">
                    {post.title}
                  </h3>
                  <p className="vintage-body text-vintage-faded leading-relaxed mb-4">
                    {post.excerpt || post.description || 'Read more...'}
                  </p>
                </Link>
                
                <div className="flex items-center justify-between pt-4">
                  <div className="flex items-center flex-wrap gap-2">
                    {post.tags && post.tags.slice(0, 5).map((tag) => (
                      <span key={tag} className="vintage-caption text-vintage-faded flex items-center">
                        <Tag size={12} className="mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link 
                    to={`/post/${post.slug}`}
                    className="text-vintage-accent hover:text-vintage-ink font-medium transition-colors flex items-center space-x-1"
                  >
                    <span>Read</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Pagination Info and Controls */}
          <div className="mt-12">
            {/* Post Count Info */}
            <div className="text-center mb-4">
              <p className="vintage-caption text-vintage-faded">
                Showing {((currentPage - 1) * 5) + 1}-{Math.min(currentPage * 5, totalPosts)} of {totalPosts} {totalPosts === 1 ? 'post' : 'posts'}
              </p>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="vintage-btn disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft size={16} />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`vintage-btn ${
                      currentPage === page 
                        ? 'bg-vintage-ink text-vintage-cream' 
                        : ''
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="vintage-btn disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <p className="vintage-body text-vintage-faded mb-4">No posts found.</p>
          <Link 
            to="/"
            className="text-vintage-accent hover:text-vintage-ink font-medium transition-colors"
          >
            Back
          </Link>
        </div>
      )}
    </div>
  );
};

export default Blog;

